import textBodyM from '@fukku/styles/texts/textBodyM.module.css'
import textHeadingM from '@fukku/styles/texts/textHeadingM.module.css'
import cx from 'classix'
import { IconCloseMedium } from 'icons/components/IconCloseMedium'
import { IconErrorL } from 'icons/components/IconErrorL'
import { IconInfoL } from 'icons/components/IconInfoL'
import { IconReportL } from 'icons/components/IconReportL'
import { type ReactElement, type ReactNode, useState } from 'react'
import { applyStylesIf } from 'utils/applyStylesIf/applyStylesIf'
import type { ClientLinkProps } from 'utils/link/client/ClientLink'
import type { ServerLinkProps } from 'utils/link/server/types'
import styles from './ContextualMessage.module.css'

const ICON_SIZE = 24

export enum CONTEXTUAL_MESSAGE_TYPE {
	ERROR = 'error',
	WARNING = 'warning',
	INFO = 'info',
	DEFAULT = 'default',
	MLY = 'mly',
}

export interface ContextualMessageProps {
	className?: string
	'data-testid'?: string
	message: ReactNode
	title?: ReactNode
	type?: CONTEXTUAL_MESSAGE_TYPE
	showIcon?: boolean
	link?: ReactElement<ClientLinkProps | ServerLinkProps>
	icon?: ReactNode
	withCloseButton?: boolean
	onClose?: () => void
}

const ContextualMessageIcons = {
	error: <IconErrorL width={ICON_SIZE} height={ICON_SIZE} />,
	warning: <IconReportL width={ICON_SIZE} height={ICON_SIZE} />,
	info: <IconInfoL width={ICON_SIZE} height={ICON_SIZE} />,
	default: <IconInfoL width={ICON_SIZE} height={ICON_SIZE} />,
}

export function ContextualMessage(
	contextualMessageProps: Readonly<ContextualMessageProps>
) {
	const {
		className,
		'data-testid': dataTestId,
		message,
		showIcon = false,
		title,
		type = CONTEXTUAL_MESSAGE_TYPE.DEFAULT,
		link,
		icon,
		withCloseButton,
		onClose,
	} = contextualMessageProps
	const [visible, setVisible] = useState(true)
	const [closingAnimation, setClosingAnimation] = useState(false)

	const handleClose = () => {
		setClosingAnimation(true)
	}

	const handleVisibility = () => {
		if (closingAnimation) {
			setVisible(false)
			onClose?.()
		}
	}

	if (!visible) {
		return null
	}

	if (type === CONTEXTUAL_MESSAGE_TYPE.MLY) {
		return (
			<div
				className={cx(styles.mly, className)}
				data-testid={dataTestId ?? `contextualMessage.${type}`}
			>
				{showIcon && <div className={styles.mlyIcon}>{icon}</div>}
				<span className={textBodyM.className}>{message}</span>
			</div>
		)
	}

	return (
		<div
			data-testid={dataTestId ?? `contextualMessage.${type}`}
			className={cx(
				styles.container,
				applyStylesIf(closingAnimation, styles.closingAnimation)
			)}
			onTransitionEnd={handleVisibility}
		>
			<div className={cx(styles.contextualMessage, styles[type], className)}>
				<div className={styles.content}>
					<div className={styles.wrapper}>
						{showIcon && (
							<span className={styles.icon}>
								{ContextualMessageIcons[type]}
							</span>
						)}
						<div className={styles.text}>
							{title && <span className={textHeadingM.className}>{title}</span>}
							<span className={textBodyM.className}>{message}</span>
						</div>
					</div>
					{link && <div className={cx(showIcon && styles.link)}>{link}</div>}
				</div>
				{withCloseButton && (
					<button
						type='button'
						data-testid='contextualMessage.close'
						className={styles.close}
						onClick={handleClose}
					>
						<IconCloseMedium lang='en' alt='Close' width={22} height={22} />
					</button>
				)}
			</div>
		</div>
	)
}
